// Charts for the Fatigue Management dashboard.
//
// All recharts, all colours from the CSS variables in fatigue-shadcn.css, and
// isAnimationActive={false} throughout — same call as the ROI rebuild: these are
// operational readouts, and animating them on every filter change makes numbers
// harder to compare, not easier.

import React from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type {
  CohortDailyPoint,
  CohortFatigueSummary,
  DerivedMetricKey,
  FatigueAction,
  FatigueProjection,
  FatigueTrendPoint,
} from '../data/types';
import { METRIC_DEFINITIONS } from '../data/methodology';
import { tierVar } from './primitives';

const AXIS_PROPS = {
  stroke: 'var(--color-border)',
  tick: { fill: 'var(--color-muted-foreground)', fontSize: 12 },
  tickLine: false,
} as const;

const TOOLTIP_PROPS = {
  contentStyle: {
    background: 'var(--color-card)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    color: 'var(--color-card-foreground)',
    fontSize: 12,
  },
  labelStyle: { color: 'var(--color-muted-foreground)' },
  itemStyle: { color: 'var(--color-card-foreground)' },
} as const;

/** One colour per indicator so the four lines stay distinguishable. */
const METRIC_COLOURS: Record<DerivedMetricKey, string> = {
  fed: 'var(--chart-primary, var(--color-primary))',
  lrm: 'var(--color-warning)',
  cdi: 'var(--color-destructive)',
  pdc: 'var(--color-success)',
};

const METRIC_KEYS: DerivedMetricKey[] = ['fed', 'lrm', 'cdi', 'pdc'];

// ── Measured trend ──────────────────────────────────────────────────────────

export const FatigueTrendChart: React.FC<{
  points: FatigueTrendPoint[];
  /** Restrict to a subset of indicators. */
  keys?: DerivedMetricKey[];
  height?: number;
}> = ({ points, keys = METRIC_KEYS, height = 280 }) => (
  <ResponsiveContainer width='100%' height={height}>
    <LineChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
      <CartesianGrid stroke='var(--color-border)' strokeDasharray='3 3' vertical={false} />
      <XAxis dataKey='period' {...AXIS_PROPS} />
      <YAxis {...AXIS_PROPS} />
      <Tooltip {...TOOLTIP_PROPS} />
      <Legend wrapperStyle={{ fontSize: 12, color: 'var(--color-muted-foreground)' }} iconType='plainline' />
      {keys.map((key) => (
        <Line
          key={key}
          type='monotone'
          dataKey={key}
          name={METRIC_DEFINITIONS[key].code}
          stroke={METRIC_COLOURS[key]}
          strokeWidth={2}
          dot={false}
          isAnimationActive={false}
        />
      ))}
    </LineChart>
  </ResponsiveContainer>
);

// ── Cohort daily signal ─────────────────────────────────────────────────────

/**
 * A fortnight of one cohort's daily averages: sleep hours against readiness.
 *
 * Two axes because the pair is the story — a team sleeping less and recovering
 * less on the same days is the shape FED is counting. Nulls are passed straight
 * through so recharts breaks the line on days nobody wore a device, rather than
 * drawing a drop to zero.
 */
export const CohortDailyChart: React.FC<{
  points: CohortDailyPoint[];
  height?: number;
}> = ({ points, height = 200 }) => (
  <ResponsiveContainer width='100%' height={height}>
    <LineChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
      <CartesianGrid stroke='var(--color-border)' strokeDasharray='3 3' vertical={false} />
      <XAxis dataKey='day' {...AXIS_PROPS} tickFormatter={(day: string) => day.slice(5)} />
      <YAxis yAxisId='sleep' domain={[0, 12]} {...AXIS_PROPS} />
      <YAxis yAxisId='readiness' orientation='right' domain={[0, 100]} {...AXIS_PROPS} />
      <Tooltip {...TOOLTIP_PROPS} />
      <Legend wrapperStyle={{ fontSize: 12, color: 'var(--color-muted-foreground)' }} iconType='plainline' />
      <Line
        yAxisId='sleep'
        type='monotone'
        dataKey='sleepHours'
        name='Sleep (h)'
        stroke={METRIC_COLOURS.fed}
        strokeWidth={2}
        dot={false}
        connectNulls={false}
        isAnimationActive={false}
      />
      <Line
        yAxisId='readiness'
        type='monotone'
        dataKey='readiness'
        name='Readiness'
        stroke={METRIC_COLOURS.pdc}
        strokeWidth={2}
        dot={false}
        connectNulls={false}
        isAnimationActive={false}
      />
    </LineChart>
  </ResponsiveContainer>
);

// ── Projection ──────────────────────────────────────────────────────────────

/**
 * "If nothing changes" projection.
 *
 * FM-11 — view="blocks": the projection is stepped through two weeks at a time,
 * because the forward half of a 24-week line was unreadable in one go.
 * FM-12 — view="full": the same projection with all 12 forward weeks visible, so
 * the whole horizon can still be seen in one chart.
 */
export const ProjectionChart: React.FC<{
  projection: FatigueProjection;
  view: 'blocks' | 'full';
  metricKey?: DerivedMetricKey;
  height?: number;
}> = ({ projection, view, metricKey = 'fed', height = 280 }) => {
  const [blockIndex, setBlockIndex] = React.useState(0);
  const block = projection.blocks[blockIndex];

  const measured = projection.points.filter((p) => !p.projected);
  const lastMeasured = measured[measured.length - 1];

  const points =
    view === 'full'
      ? projection.points
      : [
          ...measured.slice(-4),
          ...projection.points.filter((p) => p.projected && block?.weekOffsets.includes(p.weekOffset)),
        ];

  return (
    <div className='flex flex-col gap-3'>
      {view === 'blocks' && (
        <div className='flex items-center justify-between gap-3'>
          <p className='text-sm font-medium'>{block?.label ?? ''} ahead</p>
          <div className='flex items-center gap-1'>
            <Button
              variant='outline'
              size='icon'
              aria-label='Previous two weeks'
              disabled={blockIndex === 0}
              onClick={() => setBlockIndex((i) => Math.max(0, i - 1))}
            >
              <ChevronLeft className='size-4' aria-hidden />
            </Button>
            <span className='px-1 text-xs tabular-nums text-muted-foreground'>
              {blockIndex + 1} / {projection.blocks.length}
            </span>
            <Button
              variant='outline'
              size='icon'
              aria-label='Next two weeks'
              disabled={blockIndex >= projection.blocks.length - 1}
              onClick={() => setBlockIndex((i) => Math.min(projection.blocks.length - 1, i + 1))}
            >
              <ChevronRight className='size-4' aria-hidden />
            </Button>
          </div>
        </div>
      )}

      <ResponsiveContainer width='100%' height={height}>
        <AreaChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
          <defs>
            <linearGradient id='fatigue-projection-fill' x1='0' y1='0' x2='0' y2='1'>
              <stop offset='0%' stopColor={METRIC_COLOURS[metricKey]} stopOpacity={0.25} />
              <stop offset='100%' stopColor={METRIC_COLOURS[metricKey]} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke='var(--color-border)' strokeDasharray='3 3' vertical={false} />
          <XAxis dataKey='period' {...AXIS_PROPS} />
          <YAxis {...AXIS_PROPS} />
          <Tooltip {...TOOLTIP_PROPS} />
          {/* Shade the projected half so measured and modelled are never confused. */}
          {points.some((p) => p.projected) && (
            <ReferenceArea
              x1={lastMeasured?.period}
              x2={points[points.length - 1]?.period}
              fill='var(--color-muted-foreground)'
              fillOpacity={0.08}
              ifOverflow='extendDomain'
            />
          )}
          {lastMeasured && (
            <ReferenceLine
              x={lastMeasured.period}
              stroke='var(--color-muted-foreground)'
              strokeDasharray='4 4'
              label={{
                value: 'Now',
                position: 'insideTopRight',
                fill: 'var(--color-muted-foreground)',
                fontSize: 11,
              }}
            />
          )}
          <Area
            type='monotone'
            dataKey={metricKey}
            name={METRIC_DEFINITIONS[metricKey].code}
            stroke={METRIC_COLOURS[metricKey]}
            strokeWidth={2}
            fill='url(#fatigue-projection-fill)'
            dot={false}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

// ── Cohort quadrant ─────────────────────────────────────────────────────────

/**
 * Circadian disruption against load–readiness misalignment, sized by headcount.
 * Teams in the top-right are being asked for more than they can recover from on
 * a schedule that is also disrupting their sleep — the pairing that matters most
 * operationally.
 */
export const CohortQuadrantChart: React.FC<{
  summaries: CohortFatigueSummary[];
  onSelect?: (cohortId: string) => void;
  height?: number;
}> = ({ summaries, onSelect, height = 300 }) => {
  const data = summaries.map((s) => ({
    x: s.cdiScore,
    y: s.lrmPct,
    z: s.cohort.headcount,
    name: s.cohort.name,
    tier: s.riskTier,
    cohortId: s.cohort.id,
  }));

  return (
    <ResponsiveContainer width='100%' height={height}>
      <ScatterChart margin={{ top: 8, right: 16, bottom: 8, left: -16 }}>
        <CartesianGrid stroke='var(--color-border)' strokeDasharray='3 3' />
        <XAxis type='number' dataKey='x' name='Circadian disruption' domain={[0, 100]} {...AXIS_PROPS} />
        <YAxis type='number' dataKey='y' name='Load–readiness misalignment' unit='%' {...AXIS_PROPS} />
        <ZAxis type='number' dataKey='z' range={[60, 400]} name='Headcount' />
        <Tooltip {...TOOLTIP_PROPS} labelFormatter={() => ''} />
        <Scatter
          data={data}
          isAnimationActive={false}
          onClick={(point: { cohortId?: string }) => point?.cohortId && onSelect?.(point.cohortId)}
          cursor={onSelect ? 'pointer' : undefined}
        >
          {data.map((point) => (
            <Cell key={point.cohortId} fill={tierVar[point.tier]} fillOpacity={0.7} />
          ))}
        </Scatter>
      </ScatterChart>
    </ResponsiveContainer>
  );
};

// ── Action matrix ───────────────────────────────────────────────────────────

/** Impact against ease — the top-right actions are the ones to do first. */
export const ActionMatrixChart: React.FC<{
  actions: FatigueAction[];
  height?: number;
}> = ({ actions, height = 300 }) => {
  const data = actions.map((action) => ({
    x: action.ease,
    y: action.impact,
    z: 200,
    name: action.title,
  }));

  return (
    <ResponsiveContainer width='100%' height={height}>
      <ScatterChart margin={{ top: 8, right: 16, bottom: 8, left: -16 }}>
        <CartesianGrid stroke='var(--color-border)' strokeDasharray='3 3' />
        <XAxis type='number' dataKey='x' name='Ease' domain={[0, 6]} ticks={[1, 2, 3, 4, 5]} {...AXIS_PROPS} />
        <YAxis type='number' dataKey='y' name='Impact' domain={[0, 6]} ticks={[1, 2, 3, 4, 5]} {...AXIS_PROPS} />
        <ZAxis type='number' dataKey='z' range={[120, 120]} />
        <Tooltip {...TOOLTIP_PROPS} labelFormatter={() => ''} />
        <ReferenceLine x={3} stroke='var(--color-border)' />
        <ReferenceLine y={3} stroke='var(--color-border)' />
        <Scatter
          data={data}
          fill='var(--chart-primary, var(--color-primary))'
          fillOpacity={0.7}
          isAnimationActive={false}
        />
      </ScatterChart>
    </ResponsiveContainer>
  );
};
